import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { CardGroupNodeView } from './card-group-node-view'
import { nanoid } from 'nanoid'

export interface CardGroupAttributes {
  id?: string
  cols?: number
}

export const CardGroupSpec = {
  group: 'block',
  content: 'Card+',
  attrs: { id: { default: nanoid(10) } },
  toDOM() { return ['div', { 'data-card-group': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-card-group]' }],
};


declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    cardGroup: {
      /**
       * Insert a card group
       */
      insertCardGroup: (attributes?: CardGroupAttributes) => ReturnType
      /**
       * Update card group attributes
       */
      updateCardGroup: (attributes: Partial<CardGroupAttributes>) => ReturnType
    }
  }
}

export const CardGroupExtension = Node.create({
  name: 'CardGroup',

  group: 'block',

  content: 'Card+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-dg-id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
      cols: {
        default: 2,
        parseHTML: element => {
          const cols = element.getAttribute('data-cols')
          return cols ? parseInt(cols, 10) : 2
        },
        renderHTML: attributes => {
          if (!attributes.cols) {
            return {}
          }
          return {
            'data-cols': attributes.cols,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="CardGroup"]',
      },
      {
        tag: 'div.card-group-container',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 
        'data-type': 'CardGroup',
        'class': 'group/cardgroup relative my-5 p-0.5'
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CardGroupNodeView)
  },

  addCommands() {
    return {
      insertCardGroup:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              cols: 2,
              ...attributes,
            },
            content: [
              {
                type: 'Card',
                attrs: {
                  title: 'Card 1',
                },
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      
                    ],
                  },
                ],
              },
              {
                type: 'Card',
                attrs: {
                  title: 'Card 2',
                },
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      
                    ],
                  },
                ],
              },
            ],
          })
        },
      updateCardGroup:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the card group with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of a card group
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
}) 